import React from 'react';
import {
  Wrapper,
  LoaderWrapper,
  SectionWrapper,
  TableWrapper,
  TableSection,
} from './Benchmarks.styles';
import { ScrollContainer } from 'components/ScrollContainer';
import { setLocalStorageItem, getLocalStorageItem } from 'utils/localStorage';
import { ApiContext } from 'context/ApiContext';
import { getQueryDatesFromPeriod } from 'context/QueryParams';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import {
  SPEND_TYPE_OPTIONS,
  COMPARE_VALUE_OPTIONS,
  BENCHMARK_METRIC_SPEND_OPTIONS,
  COMPARE_CATEGORY_SPEND_OPTIONS,
  COMPARE_BY_OPTIONS,
  COMPARE_TYPE_OPTIONS,
  CHART_SERIES_LABELS,
  CHART_COLORS,
  CompareValueEnum,
  BENCHMARK_METRIC_LABELS,
  BENCHMARK_METRIC_TOTAL_PROPS,
  COMPARE_VALUE_LABELS,
  ComapareTypeEnum,
  BenchmarkMetricEnum,
  SPEND_TYPE_LABELS,
  SpendTypeEnum,
  CompareByEnum,
  CompareCategoryEnum,
} from 'components/Benchmarks/utils';
import isEqual from 'lodash/isEqual';
import {
  BenchmarksChart,
  ChartOptions,
  BenchmarksTable,
} from 'components/Benchmarks';
import {
  IConfig,
  ISeriesItem,
  IBenchmarksData,
} from 'components/Benchmarks/types';
import { ISettingsResponse, ICurrencyFormat } from 'utils/types';
import {
  HEADER_CURRENCIES_TEXT,
  UNITS_SIGNS,
  UNITS_DIVIDERS,
  PERMISSIONS,
} from 'utils/constants';
import {
  exportSpendBenchmarksData,
  exportHeadcountBenchmarksData,
  getHeadcountBenchmarks,
  getSpendBenchmarks,
  exportHeadcountBenchmarksTableData,
  exportSpendBenchmarksTableData,
} from 'utils/api';
import NotificationBar from 'components/NotificationBar/NotificationBar';

const LOCAL_STORAGE_KEY = 'BENCHMARKS-CHART-OPTIONS';

interface IProps {
  settings: ISettingsResponse;
  format: ICurrencyFormat;
  permissions: string[];
}

interface IState {
  config: IConfig;
  data: null | IBenchmarksData;
  isLoading: boolean;
  isSuccessNotification: boolean;
  categories: string[];
  series: ISeriesItem[];
  axisTitle: string;
  selectedCategory: string | null;
}

const getPercentageOfRevenue = (
  data: IBenchmarksData,
  revenue: number
): IBenchmarksData => {
  return {
    ...data,
    benchmarks: data.benchmarks.map((item: any) => {
      return {
        ...item,
        value: (item.value / revenue) * 100,
        topQuartileValue: (item.topQuartileValue / revenue) * 100,
        medianValue: (item.medianValue / revenue) * 100,
      };
    }),
    total: {
      ...data.total,
      value: (data.total.value / revenue) * 100,
      topQuartileValue: (data.total.topQuartileValue / revenue) * 100,
      medianValue: (data.total.medianValue / revenue) * 100,
    },
  };
};

export class Benchmarks extends React.Component<IProps, IState> {
  state = {
    config: this.getDefaultConfig(),
    data: null as IState['data'],
    isLoading: false,
    isSuccessNotification: false,
    categories: [],
    series: [],
    axisTitle: '',
    selectedCategory: null as IState['selectedCategory'],
  };

  chartRef = React.createRef<any>();

  getDefaultConfig() {
    const localStorageState = getLocalStorageItem(LOCAL_STORAGE_KEY);
    const defaultState = {
      showTotal: true,
      showPeerMedian: true,
      showTopQuartile: true,
      spendType: SPEND_TYPE_OPTIONS[0].value,
      compareCategory: COMPARE_CATEGORY_SPEND_OPTIONS[0].value,
      benchmarkMetric: BENCHMARK_METRIC_SPEND_OPTIONS[0].value,
      compareBy: COMPARE_BY_OPTIONS[0].value,
      compareType: COMPARE_TYPE_OPTIONS[0].value,
      compareValue: COMPARE_VALUE_OPTIONS[0].value,
    };

    return {
      ...defaultState,
      ...localStorageState,
    };
  }

  componentDidUpdate(prevProps: IProps, prevState: IState) {
    const { config, selectedCategory, isLoading } = this.state;
    const { settings, permissions } = this.props;
    const hasSalaryPermission = permissions.includes(PERMISSIONS.SALARY_READ);

    if (
      config.compareBy === CompareByEnum.PERCENT_OF_REVENUE &&
      !hasSalaryPermission
    ) {
      this.setState({
        config: { ...config, compareBy: CompareByEnum.COST },
      });
    }

    if (!isEqual(config, prevState.config)) {
      this.setStateToLocalStorage();
    }

    if (
      config.compareValue !== prevState.config.compareValue ||
      config.compareCategory !== prevState.config.compareCategory ||
      config.spendType !== prevState.config.spendType ||
      settings !== prevProps.settings
    ) {
      this.setState({
        selectedCategory: null,
      });

      this.fetchData();

      return;
    }

    if (
      !isLoading &&
      config.benchmarkMetric !== prevState.config.benchmarkMetric
    ) {
      this.setState({
        selectedCategory: null,
      });
    }

    if (
      !isLoading &&
      (config.showTopQuartile !== prevState.config.showTopQuartile ||
        config.showPeerMedian !== prevState.config.showPeerMedian ||
        config.compareType !== prevState.config.compareType ||
        config.compareBy !== prevState.config.compareBy ||
        config.showTotal !== prevState.config.showTotal ||
        config.benchmarkMetric !== prevState.config.benchmarkMetric ||
        selectedCategory !== prevState.selectedCategory)
    ) {
      this.setState({
        categories: this.getCategories(),
        series: this.getSeriesData(),
        axisTitle: this.getAxisTitle(),
      });
    }
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = () => {
    const { config } = this.state;
    const { settings } = this.props;

    if (!settings) {
      return;
    }

    this.setState({ isLoading: true });
    const isHeadcount = config.compareValue === CompareValueEnum.HEADCOUNT;
    const request = isHeadcount
      ? getHeadcountBenchmarks({
          ...getQueryDatesFromPeriod(settings.selectedYear),
          spendType: config.spendType,
        })
      : getSpendBenchmarks({
          ...getQueryDatesFromPeriod(settings.selectedYear),
          spendType: config.spendType,
          dimensionType: config.compareCategory,
        });

    request.then((rsp) => {
      this.setState({ data: rsp, isLoading: false }, () => {
        this.setState({
          categories: this.getCategories(),
          series: this.getSeriesData(),
          axisTitle: this.getAxisTitle(),
        });
      });
    });
  };

  getChartData = () => {
    const { config, data } = this.state;
    const isHeadcount = config.compareValue === CompareValueEnum.HEADCOUNT;

    if (!data || !data.benchmarks) {
      return null;
    }

    if (isHeadcount) {
      return data;
    }

    return {
      ...data,
      benchmarks: data.benchmarks.filter(
        (item: any) => item.provider === config.benchmarkMetric
      ),
      total: {
        medianValue:
          data.total[
            BENCHMARK_METRIC_TOTAL_PROPS[
              config.benchmarkMetric as BenchmarkMetricEnum
            ].medianValue
          ],
        topQuartileValue:
          data.total[
            BENCHMARK_METRIC_TOTAL_PROPS[
              config.benchmarkMetric as BenchmarkMetricEnum
            ].topQuartileValue
          ],
        value: data.total.value,
      },
    };
  };

  getSeriesData = () => {
    const { config, data } = this.state;
    const { format, settings } = this.props;
    const clientName = settings?.clientName;
    const year = data?.year;
    const settingsYear = settings?.selectedYear.endYear;
    const isHeadcount = config.compareValue === CompareValueEnum.HEADCOUNT;
    const isPercentage =
      !isHeadcount && config.compareBy === CompareByEnum.PERCENT_OF_REVENUE;
    let series = [] as ISeriesItem[];

    const chartData = this.getChartData();

    if (!chartData) {
      return [];
    }

    const comparedByData = isPercentage
      ? getPercentageOfRevenue(chartData, data!.revenue!)
      : chartData;

    const combinedData =
      isHeadcount || !config.showTotal
        ? comparedByData.benchmarks
        : [comparedByData.total, ...comparedByData.benchmarks];

    if (config.compareType === ComapareTypeEnum.SIDE_BY_SIDE) {
      series = [
        ...series,
        {
          name: `${clientName} (${settingsYear})`,
          id: 'client',
          index: 1,
          data: combinedData.map((item: any) => ({
            y:
              isHeadcount || isPercentage
                ? item.value
                : item.value / UNITS_DIVIDERS[format.unit],
            ...this.getDataPointOptions(item.category),
          })),
          color: CHART_COLORS[0],
        },
      ];

      if (isHeadcount) {
        series = [
          ...series,
          {
            name: `${CHART_SERIES_LABELS.benchmark} (${year})`,
            id: 'benchmark',
            index: 2,
            data: combinedData.map((item: any) => ({
              y: item.benchmarkValue,
              ...this.getDataPointOptions(item.category),
            })),
            color: CHART_COLORS[1],
          },
        ];
      } else {
        series = [
          ...series,
          {
            name: `${CHART_SERIES_LABELS.peerMedian} (${year})`,
            id: 'peerMedian',
            index: 2,
            data: combinedData.map((item: any) => ({
              y: !isPercentage
                ? item.medianValue / UNITS_DIVIDERS[format.unit]
                : item.medianValue,
              ...this.getDataPointOptions(item.category),
            })),
            color: CHART_COLORS[1],
            visible: config.showPeerMedian,
          },
          {
            name: `${CHART_SERIES_LABELS.topQuartile} (${year})`,
            id: 'topQuartile',
            index: 3,
            data: combinedData.map((item: any) => ({
              y: !isPercentage
                ? item.topQuartileValue / UNITS_DIVIDERS[format.unit]
                : item.topQuartileValue,
              ...this.getDataPointOptions(item.category),
            })),
            color: CHART_COLORS[2],
            visible: config.showTopQuartile,
          },
        ];
      }
    } else {
      if (isHeadcount) {
        series = [
          ...series,
          {
            name: `${CHART_SERIES_LABELS.benchmark} (${year})`,
            id: 'benchmark',
            index: 2,
            data: combinedData.map((item: any) => ({
              y: item.benchmarkValue - item.value,
              ...this.getDataPointOptions(item.category),
            })),
            color: CHART_COLORS[1],
          },
        ];
      } else {
        series = [
          ...series,
          {
            name: `${CHART_SERIES_LABELS.peerMedian} (${year})`,
            id: 'peerMedian',
            index: 2,
            data: combinedData.map((item: any) => ({
              y: !isPercentage
                ? (item.medianValue - item.value) / UNITS_DIVIDERS[format.unit]
                : item.medianValue - item.value,
              ...this.getDataPointOptions(item.category),
            })),
            color: CHART_COLORS[1],
            visible: config.showPeerMedian,
          },
          {
            name: `${CHART_SERIES_LABELS.topQuartile} (${year})`,
            id: 'topQuartile',
            index: 3,
            data: combinedData.map((item: any) => ({
              y: !isPercentage
                ? (item.topQuartileValue - item.value) /
                  UNITS_DIVIDERS[format.unit]
                : item.topQuartileValue - item.value,
              ...this.getDataPointOptions(item.category),
            })),
            color: CHART_COLORS[2],
            visible: config.showTopQuartile,
          },
        ];
      }
    }

    return series;
  };

  getDataPointOptions = (category: string) => {
    const { selectedCategory } = this.state;
    const isActive = !selectedCategory || category === selectedCategory;
    const opacity = isActive ? 1 : 0.4;

    return {
      opacity,
      dataLabels: {
        style: { opacity },
      },
    };
  };

  getCategories = () => {
    const { config } = this.state;
    const isHeadcount = config.compareValue === CompareValueEnum.HEADCOUNT;
    const totalLabel = `Total IT${
      config.spendType ? ` ${config.spendType}` : ''
    } spend`;

    const chartData = this.getChartData();

    if (!chartData) {
      return [];
    }

    const categories = chartData.benchmarks.map((item: any) => item.category);

    return isHeadcount || !config.showTotal
      ? categories
      : [totalLabel, ...categories];
  };

  setStateToLocalStorage = () => {
    const { config } = this.state;

    setLocalStorageItem(LOCAL_STORAGE_KEY, config);
  };

  handleConfigChange = (updates: Partial<IConfig>) => {
    const { config } = this.state;

    this.setState({
      config: {
        ...config,
        ...updates,
      },
    });
  };

  getSubtitle = () => {
    const { config } = this.state;

    if (config.compareValue === CompareValueEnum.HEADCOUNT) {
      return 'CR IT Staff Mix - Average Percentage of IT Staff';
    }

    const spendTitles = {
      [BenchmarkMetricEnum.GARTNER]:
        'IT Spending as % of Revenue (for Gartner)',
      [BenchmarkMetricEnum.COMPUTER_ECONOMICS]:
        'Total IT Spending as Percentage of Revenue (for CE)',
    } as any;

    return spendTitles[config.benchmarkMetric];
  };

  getTitle = () => {
    const { config, data } = this.state;

    return `${data?.year ? data.year : ''} ${
      COMPARE_VALUE_LABELS[config.compareValue as CompareValueEnum]
    } vs ${
      BENCHMARK_METRIC_LABELS[config.benchmarkMetric as BenchmarkMetricEnum]
    }`;
  };

  getAxisTitle = () => {
    const { format } = this.props;
    const { config } = this.state;

    const isHeadcount = config.compareValue === CompareValueEnum.HEADCOUNT;
    const isPercentage = config.compareBy === CompareByEnum.PERCENT_OF_REVENUE;

    return isHeadcount
      ? COMPARE_VALUE_LABELS[config.compareValue as CompareValueEnum]
      : !isPercentage
      ? `${HEADER_CURRENCIES_TEXT[format.currency]}, ${
          UNITS_SIGNS[format.unit]
        }`
      : '% of Revenue';
  };

  getTableTitle = () => {
    const { config } = this.state;

    const isHeadcount = config.compareValue === CompareValueEnum.HEADCOUNT;

    if (isHeadcount) {
      return `${
        config.spendType
          ? `${SPEND_TYPE_LABELS[config.spendType as SpendTypeEnum]} `
          : ''
      }Headcount and Spend Breakdown`;
    }

    return `${
      config.spendType
        ? `IT ${SPEND_TYPE_LABELS[config.spendType as SpendTypeEnum]} `
        : ''
    } Top Spend Items`;
  };

  getTabledata = () => {
    const { config, selectedCategory, data } = this.state;
    const isHeadcount = config.compareValue === CompareValueEnum.HEADCOUNT;
    const isPercentage = config.compareBy === CompareByEnum.PERCENT_OF_REVENUE;
    const tableData = (isHeadcount ? data?.breakdown : data?.topSpends) || [];

    if (isHeadcount) {
      return selectedCategory
        ? tableData.filter((item: any) => item.category === selectedCategory)
        : tableData;
    }

    let spendData = tableData.map((item: any) => {
      return {
        ...item,
        serviceTower:
          config.benchmarkMetric === BenchmarkMetricEnum.GARTNER
            ? item.serviceTowerGartner
            : item.serviceTowerComputerEconomics,
      };
    });
    if (isPercentage) {
      spendData = spendData.map((item: any) => ({
        ...item,
        cost: item.cost / (data?.revenue || 1),
      }));
    }

    return selectedCategory
      ? spendData.filter((item: any) => {
          if (config.compareCategory === CompareCategoryEnum.SUMMARY) {
            return item.expenseType === selectedCategory;
          }

          if (config.compareCategory === CompareCategoryEnum.SERVICE_TOWER) {
            return item.serviceTower === selectedCategory;
          }

          return true;
        })
      : spendData;
  };

  selectCategory = (category: string | null) => {
    this.setState({ selectedCategory: category });
  };

  handleExport = (value: string) => {
    const { settings } = this.props;
    const chartInstance = this.getChartInstance();
    if (chartInstance && value === 'png') {
      chartInstance.exportChart(
        {
          filename: `${
            settings ? `${settings.clientName} ` : ''
          }${this.getTitle()}`,
        },
        {
          chart: { width: 1200 },
        }
      );
    } else if (value === 'xlsx') {
      this.handleXLSXExport();
    }
  };

  handleXLSXExport = (isTable?: boolean) => {
    const { config, selectedCategory } = this.state;
    const { settings } = this.props;
    if (config.compareValue === CompareValueEnum.HEADCOUNT) {
      const params = {
        ...getQueryDatesFromPeriod(settings.selectedYear),
        spendType: config.spendType,
      };
      if (isTable) {
        exportHeadcountBenchmarksTableData({
          ...params,
          dimValueL1: selectedCategory,
        }).then(this.showDownloadNotification);
      } else {
        exportHeadcountBenchmarksData(params).then(
          this.showDownloadNotification
        );
      }
    } else {
      const dimensionType =
        config.compareCategory !== CompareCategoryEnum.SERVICE_TOWER
          ? 'ExpenseType'
          : CompareCategoryEnum.SERVICE_TOWER;
      const params = {
        ...getQueryDatesFromPeriod(settings.selectedYear),
        spendType: config.spendType,
        dimensionType,
        viewType: config.compareBy,
        provider: config.benchmarkMetric,
        showTotal: config.showTotal,
        peerMedian: config.showPeerMedian,
        topQaurtile: config.showTopQuartile,
      };
      if (isTable) {
        exportSpendBenchmarksTableData({
          ...params,
          dimValueL1: selectedCategory,
        }).then(this.showDownloadNotification);
      } else {
        exportSpendBenchmarksData(params).then(this.showDownloadNotification);
      }
    }
  };

  showDownloadNotification = () => {
    this.setState({ isSuccessNotification: true });
    setTimeout(() => {
      this.setState({ isSuccessNotification: false });
    }, 5000);
  };

  getChartInstance = () => {
    return (
      this.chartRef &&
      this.chartRef.current &&
      this.chartRef.current.chartRef &&
      this.chartRef.current.chartRef.current &&
      this.chartRef.current.chartRef.current.chart
    );
  };

  render() {
    const {
      config,
      data,
      isLoading,
      isSuccessNotification,
      categories,
      axisTitle,
      series,
      selectedCategory,
    } = this.state;
    const { format, settings, permissions } = this.props;
    const isHeadcount = config.compareValue === CompareValueEnum.HEADCOUNT;
    const isPercentage = config.compareBy === CompareByEnum.PERCENT_OF_REVENUE;
    const hasSalaryPermission = permissions.includes(PERMISSIONS.SALARY_READ);
    const hasBenchmarkAnalysisTablePermission = permissions.includes(
      PERMISSIONS.BENCHMARK_ANALYSIS_TABLE_READ
    );

    return (
      <ScrollContainer>
        <Wrapper>
          <SectionWrapper>
            <ChartOptions
              title={this.getTitle()}
              subtitle={this.getSubtitle()}
              onConfigChange={this.handleConfigChange}
              config={config}
              onExport={this.handleExport}
              isExportDisabled={!this.getChartInstance()}
              hasSalaryPermission={hasSalaryPermission}
            />
          </SectionWrapper>
          {!settings || !data ? (
            <LoaderWrapper>
              <ApLoaderDefault loaderText={() => <ApLoaderText />} />
            </LoaderWrapper>
          ) : (
            <React.Fragment>
              <SectionWrapper>
                <BenchmarksChart
                  categories={categories}
                  series={series}
                  axisTitle={axisTitle}
                  format={format}
                  isLoading={isLoading}
                  onCategorySelect={this.selectCategory}
                  selectedCategory={selectedCategory}
                  isSpend={!isHeadcount}
                  isPercentage={isPercentage}
                  ref={this.chartRef}
                />
              </SectionWrapper>
              {(!isHeadcount || hasBenchmarkAnalysisTablePermission) && (
                <TableWrapper>
                  <TableSection>
                    <BenchmarksTable
                      data={this.getTabledata()}
                      handleExport={this.handleXLSXExport}
                      type={config.compareValue}
                      format={format}
                      title={this.getTableTitle()}
                      isLoading={isLoading}
                      isPercentage={isPercentage}
                    />
                  </TableSection>
                </TableWrapper>
              )}
            </React.Fragment>
          )}
        </Wrapper>
        {isSuccessNotification && (
          <NotificationBar successMessage="Generating file... You will receive a notification when the file has been generated" />
        )}
      </ScrollContainer>
    );
  }
}

export default (props: Partial<IProps>) => (
  <ApiContext.Consumer>
    {({ settings, format, permissions }) => (
      <Benchmarks
        {...props}
        settings={settings}
        format={format}
        permissions={permissions}
      />
    )}
  </ApiContext.Consumer>
);
